// src/pages/portal/Favorites.tsx
//
// The signed-in account's favourites, in the customer portal.
//
// The list lives on the SERVER (keyed by the Keycloak identity), so it
// follows the account across devices and sign-outs. This page only
// RENDERS it; the write path is the same toggle used on the catalogue
// cards and the product page.
//
// The API applies the catalogue visibility rules: a product that is
// hidden (or whose brand is hidden) never appears here, even if it was
// favourited before it was hidden.

import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCcw, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { FavoriteButton } from "@/components/feature/FavoriteButton";
import { useFavorites } from "@/components/feature/FavoritesProvider";
import { mapApiProduct } from "@/lib/api";
import { formatPrice } from "@/lib/formatPrice";

/** Accent- and case-insensitive fold so ?brand=Adéo matches the
 *  brand string the API returns however it was typed. */
function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").trim().toLowerCase();
}

export function PortalFavorites() {
  const { t } = useTranslation();
  const { products, loading, error, reload } = useFavorites();
  // Optional brand filter: the brand directory's "Favorites" button
  // deep-links here with ?brand=<name> (BenchMark-avPROD#11).
  const [params] = useSearchParams();
  const brandFilter = (params.get("brand") ?? "").trim();
  const shown = brandFilter
    ? products.filter((row) => fold(row.brand_raw ?? "") === fold(brandFilter))
    : products;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-xl font-510 tracking-tight text-fg-primary">
          {t("favorites.title", "My favorites")}
        </h2>
        <p className="mt-1 max-w-2xl text-xs text-fg-tertiary">
          {t(
            "favorites.intro",
            "Products you saved. This list follows your account, so it is there on every device you sign in from.",
          )}
        </p>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-fg-tertiary">
          <RefreshCcw className="mr-2 inline h-4 w-4 animate-spin" />
          {t("favorites.loading", "Loading your favorites…")}
        </p>
      ) : error ? (
        <div className="space-y-3 rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose">
          <p>{t("favorites.loadError", "Your favorites could not be loaded. Please try again.")}</p>
          <Button size="sm" variant="secondary" onClick={() => void reload()}>
            <RefreshCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("favorites.retry", "Retry")}
          </Button>
        </div>
      ) : products.length === 0 ? (
        <EmptyState
          title={t("favorites.empty", "No favorites yet")}
          description={t("favorites.emptyHint", "Tap the heart on any product to keep it here.")}
          action={
            <Link
              to="/catalog"
              className="inline-flex h-8 items-center justify-center rounded border border-white/10 bg-accent-indigo px-3 text-xs font-510 text-white transition-colors hover:bg-accent-indigo/90"
            >
              {t("favorites.browse", "Browse the catalog")}
            </Link>
          }
        />
      ) : (
        <>
          {brandFilter && (
            <p className="text-xs text-fg-tertiary">
              {t("favorites.brandFilter", "Brand: {{brand}}", { brand: brandFilter })}
              {" · "}
              <Link
                to="/portal/favorites"
                className="font-510 text-benchmark-teal-bright hover:underline"
              >
                {t("favorites.clearBrandFilter", "Show all")}
              </Link>
            </p>
          )}
          {shown.length === 0 ? (
            <EmptyState
              title={t("favorites.emptyForBrand", "No favorites for {{brand}} yet.", { brand: brandFilter })}
              description={t("favorites.emptyForBrandHint", "Tap the heart on any product of this brand to keep it here.")}
              action={
                <Link
                  to={`/catalog?brand=${encodeURIComponent(brandFilter)}`}
                  className="inline-flex h-8 items-center justify-center rounded border border-white/10 bg-accent-indigo px-3 text-xs font-510 text-white transition-colors hover:bg-accent-indigo/90"
                >
                  {t("favorites.browseBrand", "Browse this brand")}
                </Link>
              }
            />
          ) : (
          <>
          <p className="text-xs text-fg-tertiary">
            {t("favorites.count", "{{count}} products", { count: shown.length })}
          </p>
          <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shown.map((row) => {
              const product = mapApiProduct(row);
              return (
                <li
                  key={row.product_id}
                  className="flex gap-3 rounded-lg border border-border bg-bg-surface p-3"
                >
                  <Link
                    to={`/catalog/${encodeURIComponent(product.sku)}`}
                    className="flex h-20 w-24 shrink-0 items-center justify-center overflow-hidden rounded bg-bg-elevated"
                  >
                    {product.image && product.image !== "x" ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ShoppingBag className="h-8 w-8 text-fg-quaternary" strokeWidth={1} />
                    )}
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/catalog/${encodeURIComponent(product.sku)}`}
                      className="line-clamp-2 text-sm font-510 text-fg-primary hover:underline"
                    >
                      {product.displayName ?? product.name}
                    </Link>
                    <div className="mt-1 text-xs text-fg-tertiary">
                      {product.brand}
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-510 text-fg-primary">
                        {product.priceEUR > 0
                          ? formatPrice(product.priceEUR, product.currency)
                          : t("product.priceOnRequest", "Price on request")}
                      </span>
                      <FavoriteButton
                        productId={row.product_id}
                        productName={product.displayName ?? product.name}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          </>
          )}
        </>
      )}
    </div>
  );
}
